import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarClock, AlertTriangle, Sparkles, X } from "lucide-react";

/** Prominent renewal banner shown on the Hospital Dashboard.
 *  - Only renders when days_left ≤ 30 (or expired).
 *  - Dismissible (session-only) when > 7 days left; sticky when ≤ 7 or expired.
 */
export default function RenewBanner({ userRole }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!userRole || userRole === "super_admin") return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/payments/my-plan");
        if (mounted) setData(data);
      } catch (_e) { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, [userRole]);

  if (!userRole || userRole === "super_admin" || !data) return null;
  const { plan, days_left, status, valid_until } = data;
  if (days_left == null && status !== "no_plan") return null;

  const expired = days_left <= 0 || status === "expired";
  const expiringSoon = !expired && days_left != null && days_left <= 30;
  if (!expired && !expiringSoon && status !== "no_plan") return null;
  // Allow dismissal only when > 7 days remain
  if (dismissed && days_left > 7) return null;

  if (status === "no_plan") {
    return (
      <Card className="border-amber-300 bg-amber-50/60 p-4" data-testid="dashboard-no-plan-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={20} />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800">No active subscription plan</h3>
            <p className="mt-1 text-sm text-amber-900/80">Choose a plan (or start a free trial) to unlock full access. The app is currently in read-only mode.</p>
          </div>
          <Link to="/app/my-plan">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
              <Sparkles size={14} className="mr-1.5" />Choose plan
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (expired) {
    return (
      <Card className="border-rose-300 bg-rose-50/70 p-4" data-testid="dashboard-expired-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-rose-600" size={20} />
          <div className="flex-1">
            <h3 className="font-semibold text-rose-800">Subscription expired</h3>
            <p className="mt-1 text-sm text-rose-900/80">
              Your <b>{plan?.name || "plan"}</b> expired{valid_until ? ` on ${valid_until.slice(0,10)}` : ""}. The app is in <b>read-only mode</b> — registrations, prescriptions, and billing are paused.
            </p>
          </div>
          <Link to="/app/my-plan">
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 animate-pulse">
              <Sparkles size={14} className="mr-1.5" />Renew now
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Expiring soon (≤ 30 days)
  const isCritical = days_left <= 7;
  const cardClass = isCritical
    ? "border-rose-300 bg-rose-50/60"
    : days_left <= 14
      ? "border-amber-300 bg-amber-50/60"
      : "border-sky-300 bg-sky-50/60";
  const iconColor = isCritical ? "text-rose-600" : days_left <= 14 ? "text-amber-600" : "text-sky-600";
  const headColor = isCritical ? "text-rose-800" : days_left <= 14 ? "text-amber-800" : "text-sky-800";
  const subColor = isCritical ? "text-rose-900/80" : days_left <= 14 ? "text-amber-900/80" : "text-sky-900/80";
  const btnClass = isCritical
    ? "bg-rose-600 hover:bg-rose-700"
    : days_left <= 14
      ? "bg-amber-600 hover:bg-amber-700"
      : "bg-brand hover:bg-brand-700";

  return (
    <Card className={`${cardClass} p-4 relative`} data-testid="dashboard-renew-banner">
      {days_left > 7 && (
        <button onClick={() => setDismissed(true)} className="absolute right-2 top-2 text-ink-muted hover:text-ink" data-testid="dashboard-renew-dismiss" title="Dismiss until next page refresh">
          <X size={16} />
        </button>
      )}
      <div className="flex items-start gap-3 pr-8">
        <CalendarClock className={`mt-0.5 shrink-0 ${iconColor}`} size={20} />
        <div className="flex-1">
          <h3 className={`font-semibold ${headColor}`}>
            {days_left} day{days_left === 1 ? "" : "s"} left on your <span className="capitalize">{plan?.name || "plan"}</span>
          </h3>
          <p className={`mt-1 text-sm ${subColor}`}>
            Expires on <b>{valid_until?.slice(0,10) || "—"}</b>. Renew now to avoid service interruption — your data stays intact during renewal.
          </p>
        </div>
        <Link to="/app/my-plan">
          <Button size="sm" className={btnClass}>
            <Sparkles size={14} className="mr-1.5" />Renew now
          </Button>
        </Link>
      </div>
    </Card>
  );
}
